<template>
  <div class="w-full p-8 gap-8 flex flex-col bg-[#F1F1F1]">
    <div
      ref="header"
      class="w-full gap-2 flex flex-col items-center justify-center text-center"
    >
      <span class="text-3xl font-bold text-gray-900">{{
        $t('auth.code.title')
      }}</span>
      <span
        class="text-[#7C7C7C] leading-5"
        v-html="$t('auth.code.description', { email })"
      />
    </div>

    <BaseOtpInput
      :key="otpKey"
      class="mx-auto"
      :num-inputs="NUM_INPUTS"
      :should-auto-focus="true"
      @on-change="handleInput"
      @on-complete="handleSubmit"
    />

    <template v-if="error">
      <span class="text-error text-center">
        {{ error.message }}
      </span>

      <BaseButton
        :is-disabled="isResendDisabled"
        color="transparent"
        @click="handleResendCode"
      >
        {{
          resendTimer > 0
            ? $t('auth.code.resendTimer', { time: resendTimer })
            : $t('auth.code.resendCode')
        }}
      </BaseButton>
    </template>

    <div class="gap-4 flex flex-col">
      <BaseButton
        class="mx-auto"
        :is-loading="isLoading"
        :is-disabled="isSubmitDisabled"
        @click="handleSubmit"
      >
        {{ $t('auth.code.login') }}
      </BaseButton>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

import * as services from '@/services'

import BaseButton from '@/components/Base/BaseButton.vue'
import BaseOtpInput from '@/components/Base/BaseOptInput/BaseOtpInput.vue'

const NUM_INPUTS = 5
const RESEND_TIMER_DURATION = 20

export default {
  name: 'AuthCode',

  components: { BaseButton, BaseOtpInput },

  props: {
    email: { type: String, required: true }
  },

  data: () => ({
    form: {
      code: ''
    },

    otpKey: 0,

    error: null,
    isResendDisabled: true,
    resendTimer: RESEND_TIMER_DURATION,
    resendInterval: null,

    isLoading: false
  }),

  computed: {
    ...mapGetters('locale', ['currentLocale']),

    isSubmitDisabled() {
      return this.form.code.length !== NUM_INPUTS
    }
  },

  created() {
    this.NUM_INPUTS = NUM_INPUTS
  },

  beforeDestroy() {
    clearInterval(this.resendInterval)
  },

  methods: {
    ...mapActions('auth', {
      login: 'LOGIN'
    }),
    ...mapActions('user', {
      updateSectionRecords: 'UPDATE_SECTION_RECORDS'
    }),

    handleInput(value) {
      this.form.code = value
    },

    async handleSubmit(code = '') {
      this.$gtag.event('auth_code_submitted')
      this.error = null

      const payload = {
        email: this.email,
        code: this.form.code || code
      }

      this.isLoading = true
      const { isSuccess, error } = await this.login({ payload, type: 'email' })

      await this.setSectionRecordsIfNeeded()

      this.isLoading = false

      if (error) {
        this.error = error
        !this.resendInterval && this.startResendTimer()
      }

      if (!isSuccess) return

      this.$gtag.event('auth_code_success')
      this.$emit('success')
    },

    async handleResendCode() {
      this.otpKey += 1
      this.error = null
      this.form.code = ''

      this.isLoading = true
      await services.auth.generateOtp({
        email: this.email,
        locale: this.currentLocale
      })
      this.isLoading = false

      this.startResendTimer()
    },

    async setSectionRecordsIfNeeded() {
      if (this.$route.query.sectionRecords) {
        const sectionRecords = JSON.parse(this.$route.query.sectionRecords)

        this.$router.replace({
          query: { ...this.$route.query, sectionRecords: undefined }
        })

        await this.updateSectionRecords(sectionRecords)
      }
    },

    startResendTimer() {
      this.isResendDisabled = true
      this.resendTimer = RESEND_TIMER_DURATION

      clearInterval(this.resendInterval)
      this.resendInterval = setInterval(() => {
        this.resendTimer--
        if (this.resendTimer <= 0) {
          this.isResendDisabled = false
          clearInterval(this.resendInterval)
        }
      }, 1000)
    }
  }
}
</script>
